import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UiModalReminder from "../../../../components/modal-reminder/ModalReminder.component";
import { ModalReminderMode } from "../../../../models/components/modal-reminder.model";
import { IReminder, Reminder } from "../../../../models/entities/reminder.model";
import { useMyReminders } from "../../../../hooks/useMyReminders";
import { useResolveReminder } from "../../../../hooks/useResolveReminder";
import { getSessionStorageUser } from "../../../../services/session/auth.service";
import WorklistSection from "../WorklistSection";
import { useWorklist } from "../WorklistContext";
import ReminderRow from "./ReminderRow";
import { groupReminders } from "./reminders.service";

export const REMINDERS_SECTION_ID = "reminders";

/**
 * "Reminders" — the second section of Home's Today's worklist (KAN-2555).
 *
 * Two groups: Today (due on the coach's LOCAL date, participant name A–Z) and
 * Past unresolved (due earlier, within the last 30 days, oldest first). The
 * Past header is hidden when that group is empty; Today shows
 * "No reminders due today" when it is.
 *
 * The count badge is every row the section renders — today + past-30 (Maria,
 * 2026-09-16 09:39). That is also the number published to `WorklistContext`,
 * so the page-wide row numbering stays correct without a separate badge count.
 * It is computed from the rendered rows rather than from
 * `GET /api/reminders/count`, so the badge can never disagree with the list.
 */
const RemindersSection = () => {
	const { t } = useTranslation("common");
	const sessionUser = getSessionStorageUser();

	// One clock read for the whole section, taken on mount and passed down, so
	// the request bounds, the grouping and every row's "Today" label are all
	// resolved against the same instant — and each row stays a pure render.
	const today = useMemo(() => new Date(), []);

	const { reminders, isLoading, isError, invalidate } = useMyReminders(sessionUser?.id, today);
	const resolveMutation = useResolveReminder();
	const { getRowOffset } = useWorklist();

	const [selectedReminder, setSelectedReminder] = useState<IReminder | null>(null);

	const grouped = useMemo(() => groupReminders(reminders, today), [reminders, today]);
	const renderedCount = grouped.today.length + grouped.past.length;

	const rowOffset = getRowOffset(REMINDERS_SECTION_ID);

	const closeModal = () => setSelectedReminder(null);
	const completeModal = (refresh: boolean) => {
		setSelectedReminder(null);
		if (refresh) invalidate();
	};

	const renderRow = (reminder: IReminder, position: number, isDueToday: boolean) => (
		<ReminderRow
			key={reminder.id}
			reminder={reminder}
			index={rowOffset + position + 1}
			isDueToday={isDueToday}
			onEdit={setSelectedReminder}
			onResolve={(row) => resolveMutation.mutate(row)}
			isResolving={resolveMutation.isPending}
		/>
	);

	return (
		<>
			<WorklistSection
				sectionId={REMINDERS_SECTION_ID}
				titleKey="HOME.WORKLIST.REMINDERS.TITLE"
				count={renderedCount}
				isLoading={isLoading}
				isError={isError}
				// Both groups empty: the section renders the Today empty line
				// rather than a generic "no results", and NEVER when isError.
				isEmpty={renderedCount === 0}
				emptyMessageKey="HOME.WORKLIST.REMINDERS.EMPTY_TODAY"
				errorMessageKey="HOME.WORKLIST.REMINDERS.ERROR"
				loadingMessageKey="HOME.WORKLIST.REMINDERS.LOADING"
			>
				<div
					className="worklist-group"
					data-testid="reminders-group-today"
				>
					<h4 className="worklist-group__title">
						{t("HOME.WORKLIST.REMINDERS.GROUP_TODAY")}
					</h4>
					{grouped.today.length === 0 ? (
						<p className="worklist-section__message">
							{t("HOME.WORKLIST.REMINDERS.EMPTY_TODAY")}
						</p>
					) : (
						<ul className="worklist-rows">
							{grouped.today.map((reminder, position) =>
								renderRow(reminder, position, true)
							)}
						</ul>
					)}
				</div>

				{/* Hidden entirely when there is nothing overdue (Maria's AC 4). */}
				{grouped.past.length > 0 && (
					<div
						className="worklist-group"
						data-testid="reminders-group-past"
					>
						<h4 className="worklist-group__title">
							{t("HOME.WORKLIST.REMINDERS.GROUP_PAST")}
						</h4>
						<ul className="worklist-rows">
							{grouped.past.map((reminder, position) =>
								renderRow(reminder, grouped.today.length + position, false)
							)}
						</ul>
					</div>
				)}
			</WorklistSection>

			{/* The existing Edit Reminder modal, rendered once for the section. */}
			{selectedReminder && (
				<UiModalReminder
					mode={ModalReminderMode.UPDATE}
					entity={selectedReminder as Reminder}
					isVisible={true}
					onCancel={closeModal}
					onComplete={completeModal}
				/>
			)}
		</>
	);
};

export default RemindersSection;
